import React, { FC, ReactElement, }  from "react";
import { classNames, FormControlSize, getFormSizeSuffix, } from ".";

export enum ButtonColor {
    Primary = "Primary",
    Secondary = "Secondary",
    Success = "Success",
    Danger = "Danger",
    Warning = 'Warning',
    Info = "Info",
    Light = "Light",
    Dark = "Dark",
    Custom = "Custom",
};

export enum ButtonStyle {
    Default = "Default",
    Outline = "Outline",
    Inline = "Inline",
    Badge = "Badge",
    Link = "Link",
    Close = "Close",
    Pill = "Pill",
};

interface Props {
    label?: string;
    title?: string;
    name?: string;
    className?: string | string[];
    readonly?: boolean;
    disabled?: boolean;
    color?: ButtonColor;
    style?: ButtonStyle;
    size?: FormControlSize;
    content?: React.ReactNode;
    isMenuItem?: boolean;

    hideLabel?: boolean;

    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;

    children?: React.ReactNode;
}

export type { Props as ButtonProps };

export const Button: FC<Props> = (props): ReactElement => {
    const { label, title, name, disabled, onClick, hideLabel, children, } = props;

    const css = getCss(props);

    const labelElement = hideLabel ? <></> : <span>{label}</span>;

    return (
        <button
            type="button"
            className={css}
            disabled={disabled}
            name={name}
            title={title}
            onClick={onClick}
            aria-label={label}
        >
            {children || labelElement}
        </button>
    );
}

const getCss = (props: Props): string => {
    const { color, style, className, size, disabled, isMenuItem, } = props;
    const css = className ? (Array.isArray(className) ? classNames([...className]) : className) : null;

    // menu items are handled specially
    if (isMenuItem) {
        return classNames([
            "dropdown-item",
            css,
            disabled ? "disabled" : null,
        ]);
    }

    const btnSize = (size === FormControlSize.Small) || (size === FormControlSize.Large) ? size : null;

    return classNames([
        getClassName(color, style),
        btnSize && `btn-${getFormSizeSuffix(btnSize)}`,
        css,
        disabled ? "disabled" : null,
    ]);
}

const getClassName = (
    color: ButtonColor = ButtonColor.Primary,
    style: ButtonStyle = ButtonStyle.Default,
    size: FormControlSize = FormControlSize.Medium,
): string => {

    let colorCss = color.toString().toLowerCase();

    // determine the size class, if any
    const sizeSuffix = getFormSizeSuffix(size);
    const sizeCss = sizeSuffix ? `btn-${sizeSuffix}` : null;

    // compose the button style
    let css = "";
    switch (style) {
        case ButtonStyle.Outline:
            css = `btn-outline-${colorCss}`;
            break;

        case ButtonStyle.Inline:
            css = `btn-inline link-${colorCss}`;
            break;

        case ButtonStyle.Link:
            css = `btn-link link-${colorCss}`;
            break;

        case ButtonStyle.Pill:
            css = `btn-${colorCss} btn-pill`;
            break;

        case ButtonStyle.Badge:
            css = `btn-${colorCss}`;
            break;

        case ButtonStyle.Close:
            css = "btn-close";
            break;

        case ButtonStyle.Default:
        default:
            css = `btn-${colorCss}`;
    }

    return classNames([
        (style === ButtonStyle.Close) ? null : "btn",
        css,
        sizeCss,
        (style === ButtonStyle.Badge) ? "badge" : null,
    ]);
};